import { useEffect, type ReactNode } from "react";

import { useThemeStore } from "./themeStore";

/**
 * Aplica el tema claro/oscuro (clase `.dark` + `color-scheme` en `<html>`).
 *
 * Acá vivía también `setColorSchema`, el "tema de la organización" del fork: tomaba un
 * color elegido en Configuración, derivaba una rampa HSL y la escribía INLINE en `<html>`
 * (`--primary-50…950`, `--contrast`). Un estilo inline le gana a `:root` y a `.dark`, así
 * que un solo guardado pisaba la marca y dejaba al modo oscuro sin su rampa. AcademIA
 * tiene un color de marca fijo (#C0392B, ver index.css), así que la función, su selector y
 * la mutación que lo persistía se sacaron.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useThemeStore((state) => state.theme);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (typeof window.matchMedia !== "function") {
      document.documentElement.classList.toggle("dark", theme === "dark");
      document.documentElement.style.colorScheme = theme === "dark" ? "dark" : "light";
      return;
    }

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const applyTheme = () => {
      const useDark = theme === "dark" || (theme === "system" && media.matches);
      document.documentElement.classList.toggle("dark", useDark);
      document.documentElement.style.colorScheme = useDark ? "dark" : "light";
    };

    applyTheme();
    if (theme !== "system") return;

    media.addEventListener("change", applyTheme);
    return () => media.removeEventListener("change", applyTheme);
  }, [theme]);

  return <>{children}</>;
}
